/**
 * Cocina (KDS): qué órdenes van a cocina, qué unidades se preparan ahí y
 * cómo se imprime la comanda. Funciones puras: nada de Angular ni de
 * Firestore.
 *
 * Un producto lleva `kitchen: true` en su documento cuando se prepara en
 * cocina. La caja (y la app) copian el producto en cada línea de la orden
 * (`order.bricks[].brick`), así que la línea ya sabe si es de cocina.
 *
 * Cocina trabaja por UNIDAD, no por orden: cada hot dog es un e-ticket que
 * se imprime, se prepara y se entrega por separado (tres hot dogs de una
 * misma orden son tres tarjetas y tres comandas). El estado de cada unidad
 * vive en la orden, en `kitchen_units.<id>` (id = `u<línea>_<n>`, estable
 * porque las líneas de una orden no cambian), y el agregado va en
 * `kitchen_status` y en `status` para el monitor y los informes.
 *
 * Las órdenes llegan por dos caminos:
 *  - La caja del monitor (efectivo/POS): una orden con `event_key`, nace
 *    `pending` con `kitchen: true` y `quantity` N en la línea.
 *  - La app Barlleno (tarjeta): la función splitTicketsEvents parte la orden
 *    en una orden hija por e-ticket (`isEticket`, `quantity: 1`, `status:
 *    making`, número propio que va en el QR del correo) y marca la madre con
 *    `orderSplit`. En cocina cuentan las hijas; la madre partida no.
 */

import { lineName } from '../event-products/product-variants';

/** pending → preparing → delivered. Solo las dos primeras se ven en cocina. */
export type KitchenStatus = 'pending' | 'preparing' | 'delivered';

/**
 * Estados de orden (los del monitor) con los que una orden sigue viva para
 * cocina. Las de la app nacen `making` (lo pone la función al partirlas);
 * `way` ("lista") lo puede poner la caja sin que cocina haya terminado.
 */
export const ACTIVE_ORDER_STATUSES = ['pending', 'making', 'way'];

/** Estado de la orden que corresponde a cada estado agregado de cocina. */
export const ORDER_STATUS_BY_KITCHEN: { [status in KitchenStatus]: string } = {
    pending: 'pending',
    preparing: 'making',
    delivered: 'delivered',
};

/** Minutos desde que entró la orden a partir de los cuales se resalta. */
export const KITCHEN_WARN_MINUTES = 10;
export const KITCHEN_LATE_MINUTES = 20;

export interface KitchenLine {
    /** Posición de la línea en `order.bricks`: de aquí sale el id de sus unidades. */
    lineIndex: number;
    name: string;
    presentation: string;
    quantity: number;
    /** Zona (categoría) del producto, para órdenes de la app sin `event_category_key`. */
    category: string;
}

/** Una unidad de cocina: un hot dog, un e-ticket, una tarjeta. */
export interface KitchenUnit {
    /** `u<línea>_<n>`; llave del mapa `kitchen_units` de la orden. */
    id: string;
    /** `<orden>/<id>`: única en pantalla. */
    key: string;
    order: any;
    name: string;
    presentation: string;
    category: string;
    /** 1..N dentro de la orden, para "2/3". */
    index: number;
    /** Unidades de cocina de la orden. */
    total: number;
    status: KitchenStatus;
    started_at: Date | null;
    delivered_at: Date | null;
    by: string;
    prints: number;
}

/** Una línea de la orden tal como la guarda la caja: la presentación con el producto dentro. */
export function isKitchenLine(line: any): boolean {
    return !!(line && line.brick && line.brick.kitchen === true);
}

/** Alguna línea se prepara en cocina. */
export function isKitchenOrder(order: any): boolean {
    return !!(order && Array.isArray(order.bricks) && order.bricks.some(isKitchenLine));
}

/** Líneas de cocina de la orden, listas para la pantalla y la comanda. */
export function kitchenLines(order: any): KitchenLine[] {
    if (!order || !Array.isArray(order.bricks)) return [];
    const lines: KitchenLine[] = [];
    order.bricks.forEach((line: any, lineIndex: number) => {
        if (!isKitchenLine(line)) return;
        lines.push({
            lineIndex,
            // Con su variante ("Squeeze · Whiskey"): cocina tiene que saber cuál preparar.
            name: lineName(line),
            presentation: String(line.presentation || ''),
            quantity: lineQuantity(line),
            category: String((line.brick && line.brick.category) || ''),
        });
    });
    return lines;
}

/** Unidades enteras y al menos una: la caja no vende medias unidades. */
function lineQuantity(line: any): number {
    const raw = Number(line && line.quantity);
    return isNaN(raw) || raw < 1 ? 1 : Math.round(raw);
}

/** Cuántas líneas de la orden NO son de cocina (bebidas de barra, etc.). */
export function otherLinesCount(order: any): number {
    if (!order || !Array.isArray(order.bricks)) return 0;
    return order.bricks.filter(line => !isKitchenLine(line)).length;
}

export function unitId(lineIndex: number, n: number): string {
    return `u${lineIndex}_${n}`;
}

export function unitKey(orderKey: string, id: string): string {
    return `${orderKey}/${id}`;
}

/** El mapa `kitchen_units` de la orden, si cocina ya trabajó por unidad. */
function storedUnits(order: any): any {
    const units = order && order.kitchen_units;
    return units && typeof units === 'object' ? units : null;
}

/** Lo guardado en `kitchen_units.<id>`, si existe. */
function storedUnit(order: any, id: string): any {
    const units = storedUnits(order);
    return units && units[id] && typeof units[id] === 'object' ? units[id] : null;
}

/**
 * Estado de una unidad. Con `kitchen_units` en la orden manda su registro
 * (sin registro es pendiente). Sin `kitchen_units` se hereda el estado de la
 * orden: las que cocina tomó antes de trabajar por unidad traen solo
 * `kitchen_status`, y las de la app no traen nada (pendientes).
 */
export function unitStatus(order: any, id: string): KitchenStatus {
    const stored = storedUnit(order, id);
    const raw = stored ? stored.status : (storedUnits(order) ? 'pending' : (order && order.kitchen_status));
    return raw === 'preparing' || raw === 'delivered' ? raw : 'pending';
}

/** Todas las unidades de cocina de la orden, en el orden de sus líneas. */
export function kitchenUnits(order: any): KitchenUnit[] {
    const lines = kitchenLines(order);
    const total = lines.reduce((sum, l) => sum + l.quantity, 0);
    const orderKey = String((order && order.$key) || '');
    const units: KitchenUnit[] = [];
    let index = 0;
    lines.forEach(line => {
        for (let n = 0; n < line.quantity; n++) {
            const id = unitId(line.lineIndex, n);
            const stored = storedUnit(order, id) || {};
            index += 1;
            units.push({
                id,
                key: unitKey(orderKey, id),
                order,
                name: line.name,
                presentation: line.presentation,
                category: line.category,
                index,
                total,
                status: unitStatus(order, id),
                started_at: toDate(stored.started_at),
                delivered_at: toDate(stored.delivered_at),
                by: String(stored.by || ''),
                prints: Number(stored.prints || 0),
            });
        }
    });
    return units;
}

/** Estado de la orden a partir del de sus unidades. */
export function aggregateStatus(statuses: KitchenStatus[]): KitchenStatus {
    if (statuses.length === 0) return 'pending';
    if (statuses.every(s => s === 'delivered')) return 'delivered';
    if (statuses.every(s => s === 'pending')) return 'pending';
    return 'preparing';
}

/** Estado agregado de cocina de la orden tal como está ahora. */
export function orderKitchenStatus(order: any): KitchenStatus {
    return aggregateStatus(kitchenUnits(order).map(u => u.status));
}

/** Orden hija de la app: un e-ticket con número propio (el del QR del correo). */
export function isEticketOrder(order: any): boolean {
    return !!(order && order.isEticket === true);
}

/**
 * Orden de la app partida en hijas por la función: sus unidades ya viven en
 * las hijas, así que la madre no se prepara.
 */
export function isSplitParent(order: any): boolean {
    return !!(order && (order.orderSplit === true || order.splitOrder === true));
}

/**
 * La orden que ya no interesa en pantalla: cancelada, eliminada, entregada
 * (aunque la haya entregado el monitor y no cocina), madre partida, con el
 * pago revertido o sin líneas de cocina.
 */
export function isLiveKitchenOrder(order: any): boolean {
    if (!order) return false;
    if (order.deleted === true) return false;
    if (ACTIVE_ORDER_STATUSES.indexOf(String(order.status)) < 0) return false;
    if (isSplitParent(order)) return false;
    if (order.transaction_status === 'reverted') return false;
    return isKitchenOrder(order);
}

/** Fecha como Date, venga como Timestamp de Firestore, Date o texto. */
export function toDate(raw: any): Date | null {
    if (!raw) return null;
    if (raw instanceof Date) return raw;
    if (typeof raw.toDate === 'function') return raw.toDate();
    // Timestamp serializado por una función (`{_seconds, _nanoseconds}`) o
    // por JSON (`{seconds, nanoseconds}`).
    const seconds = raw._seconds !== undefined ? raw._seconds : raw.seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
}

/** Fecha de la orden. */
export function orderDate(order: any): Date | null {
    return toDate(order && order.added_date);
}

/** Minutos enteros transcurridos desde `from` hasta `now` (nunca negativo). */
export function minutesSince(from: Date | null, now: Date): number {
    if (!from) return 0;
    return Math.max(0, Math.floor((now.getTime() - from.getTime()) / 60000));
}

/** "hace 3 min", "hace 1 h 05 min", "ahora". */
export function elapsedLabel(from: Date | null, now: Date): string {
    const minutes = minutesSince(from, now);
    if (minutes <= 0) return 'ahora';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours < 24) return `hace ${hours} h ${rest < 10 ? '0' + rest : rest} min`;
    const days = Math.floor(hours / 24);
    return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
}

/** 'ok' | 'warn' | 'late' según el tiempo de espera. */
export function urgency(from: Date | null, now: Date): 'ok' | 'warn' | 'late' {
    const minutes = minutesSince(from, now);
    if (minutes >= KITCHEN_LATE_MINUTES) return 'late';
    if (minutes >= KITCHEN_WARN_MINUTES) return 'warn';
    return 'ok';
}

/**
 * Desde cuándo espera una unidad: en preparación, desde que se tomó; pendiente,
 * desde que entró la orden. Un e-ticket de la app pendiente no espera a
 * nadie (se compró días antes y se canjea cuando el cliente llega), así que
 * no tiene urgencia.
 */
export function unitWaitingSince(unit: KitchenUnit): Date | null {
    if (unit.status === 'preparing') return unit.started_at || orderDate(unit.order);
    if (isEticketOrder(unit.order)) return null;
    return orderDate(unit.order);
}

/**
 * Las de la caja (el cliente está en la ventanilla) antes que los e-tickets
 * de la app (se canjean cuando el cliente llega); dentro de cada grupo la
 * orden más antigua primero y, en la misma orden, por número de unidad.
 */
export function sortUnits(units: KitchenUnit[]): KitchenUnit[] {
    return units.slice().sort((a, b) => {
        const ea = isEticketOrder(a.order) ? 1 : 0;
        const eb = isEticketOrder(b.order) ? 1 : 0;
        if (ea !== eb) return ea - eb;
        const da = orderDate(a.order), db = orderDate(b.order);
        const diff = (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
        if (diff !== 0) return diff;
        if (a.order.$key !== b.order.$key) return String(a.order.$key).localeCompare(String(b.order.$key));
        return a.index - b.index;
    });
}

/** Unidades de todas las órdenes vivas, ya ordenadas para la pantalla. */
export function activeUnits(orders: any[]): KitchenUnit[] {
    const units: KitchenUnit[] = [];
    orders.forEach(order => {
        if (!isLiveKitchenOrder(order)) return;
        kitchenUnits(order).forEach(u => {
            if (u.status !== 'delivered') units.push(u);
        });
    });
    return sortUnits(units);
}

/** Texto normalizado para buscar por número de orden o producto. */
function searchable(unit: KitchenUnit): string {
    return [unit.order.number, unit.order.number_original, unit.name, unit.presentation]
        .map(v => String(v === null || v === undefined ? '' : v).toLowerCase())
        .join(' ');
}

/** Filtra por lo que escribió el chef: número de orden (el del QR) o producto. */
export function filterUnits(units: KitchenUnit[], term: string): KitchenUnit[] {
    const words = String(term || '').toLowerCase().split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return units;
    return units.filter(u => {
        const text = searchable(u);
        return words.every(w => text.indexOf(w) >= 0);
    });
}

// ─── Escrituras ────────────────────────────────────────────────────────────

/**
 * Cambio de estado de una unidad como actualización de Firestore (rutas con
 * punto: solo se tocan los campos de esa unidad y el agregado). El estado de
 * la orden sigue al agregado, salvo que la caja ya la haya puesto "lista"
 * (`way`) y cocina siga preparando: ahí no se le quita.
 */
export function unitStatusUpdate(order: any, id: string, status: KitchenStatus, by: string, now: Date): any {
    const statuses: { [id: string]: KitchenStatus } = {};
    kitchenUnits(order).forEach(u => statuses[u.id] = u.status);
    statuses[id] = status;
    const aggregate = aggregateStatus(Object.keys(statuses).map(k => statuses[k]));

    const data: any = {
        [`kitchen_units.${id}.status`]: status,
        [`kitchen_units.${id}.updated_at`]: now,
        [`kitchen_units.${id}.by`]: by,
        kitchen: true,
        kitchen_status: aggregate,
        kitchen_updated_at: now,
        kitchen_by: by,
    };
    if (status === 'preparing') data[`kitchen_units.${id}.started_at`] = now;
    if (status === 'delivered') data[`kitchen_units.${id}.delivered_at`] = now;
    if (aggregate === 'preparing' && !(order && order.kitchen_started_at)) data.kitchen_started_at = now;
    if (aggregate === 'delivered') data.kitchen_delivered_at = now;
    Object.assign(data, orderStatusUpdate(order, aggregate, now));
    return data;
}

/** `status` y su hora si el agregado los cambia; vacío si se dejan como están. */
function orderStatusUpdate(order: any, aggregate: KitchenStatus, now: Date): any {
    const target = ORDER_STATUS_BY_KITCHEN[aggregate];
    const current = String((order && order.status) || '');
    if (current === target) return {};
    if (current === 'way' && target === 'making') return {};
    return { status: target, [`status_time.${target}`]: now };
}

/**
 * Si el agregado guardado no coincide con las unidades (dos tablets tocaron
 * la misma orden a la vez), la actualización que lo corrige; null si todo
 * cuadra o cocina nunca tocó la orden.
 */
export function reconcileUpdate(order: any, by: string, now: Date): any | null {
    if (!order || !order.kitchen_units || typeof order.kitchen_units !== 'object') return null;
    const aggregate = orderKitchenStatus(order);
    if (order.kitchen_status === aggregate) return null;
    return {
        kitchen_status: aggregate,
        kitchen_updated_at: now,
        kitchen_by: by,
        ...orderStatusUpdate(order, aggregate, now),
    };
}

/** Constancia de impresión de la comanda de una unidad. */
export function printUpdate(unit: KitchenUnit, now: Date): any {
    return {
        [`kitchen_units.${unit.id}.printed_at`]: now,
        [`kitchen_units.${unit.id}.prints`]: unit.prints + 1,
        kitchen_printed_at: now,
    };
}

// ─── Comanda ───────────────────────────────────────────────────────────────

function two(n: number): string {
    return n < 10 ? '0' + n : String(n);
}

export function comandaTime(d: Date): string {
    return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** "#1234" o "#1234 · 2/3" cuando la orden trae más de una unidad. */
export function unitLabel(unit: KitchenUnit): string {
    const number = unit.order.number || unit.order.$key || '';
    return unit.total > 1 ? `#${number} · ${unit.index}/${unit.total}` : `#${number}`;
}

/**
 * Contenido pdfmake de la comanda de UNA unidad para impresora de tiquetes
 * (80 mm), en letra grande: solo lo que cocina necesita. Sin precios.
 */
export function buildComandaContent(unit: KitchenUnit, zoneName: string, printedAt: Date, reprint: boolean = false): any[] {
    const order = unit.order;
    const created = orderDate(order);
    const content: any[] = [
        { text: 'COMANDA COCINA', style: 'kdsTitle' },
        { text: `ORDEN ${unitLabel(unit)}`, style: 'kdsNumber' },
        {
            text: [
                { text: 'Zona: ', bold: true }, zoneName || 'Sin zona', '\n',
                { text: isEticketOrder(order) ? 'E-ticket comprado: ' : 'Hora orden: ', bold: true }, created ? comandaTime(created) : '—', '\n',
                { text: reprint ? 'Reimpresa: ' : 'Impresa: ', bold: true }, comandaTime(printedAt),
            ],
            style: 'kdsMeta',
        },
        { canvas: [{ type: 'line', x1: 0, y1: 4, x2: 215, y2: 4, lineWidth: 1.2 }], margin: [0, 4, 0, 6] },
        {
            columns: [
                { width: 34, text: '1×', style: 'kdsQty' },
                {
                    width: '*',
                    stack: [
                        { text: unit.name.toUpperCase(), style: 'kdsItem' },
                        unit.presentation ? { text: unit.presentation, style: 'kdsPresentation' } : null,
                    ].filter(x => !!x),
                },
            ],
            margin: [0, 0, 0, 6],
        },
        { canvas: [{ type: 'line', x1: 0, y1: 4, x2: 215, y2: 4, lineWidth: 1.2 }], margin: [0, 2, 0, 6] },
    ];
    if (unit.total > 1) {
        content.push({ text: `Unidad ${unit.index} de ${unit.total} de esta orden`, style: 'kdsMeta' });
    }
    const others = otherLinesCount(order);
    if (others > 0) {
        content.push({ text: `+ ${others} ${others === 1 ? 'línea de barra (no se prepara aquí)' : 'líneas de barra (no se preparan aquí)'}`, style: 'kdsNote' });
    }
    return content;
}

/** Estilos de la comanda; se suman a los del tiquete de la caja. */
export const COMANDA_STYLES = {
    kdsTitle: { fontSize: 11, bold: true, alignment: 'center', margin: [0, 0, 0, 2] },
    kdsNumber: { fontSize: 20, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
    kdsMeta: { fontSize: 9, lineHeight: 1.25 },
    kdsQty: { fontSize: 16, bold: true },
    kdsItem: { fontSize: 13, bold: true },
    kdsPresentation: { fontSize: 9, color: '#444444' },
    kdsNote: { fontSize: 8.5, italics: true, color: '#444444', margin: [0, 4, 0, 0] },
};
